import React, { useState, useEffect } from "react";
import API from "../../api/api";
import { FaTrash, FaEdit, FaPlus, FaSave, FaTimes, FaBullhorn, FaLink, FaSortNumericDown, FaToggleOn, FaToggleOff } from "react-icons/fa";
import Navbar from "../../components/Navbar";

const ManageAnnouncements = () => {
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editId, setEditId] = useState(null);

  // Form State
  const [text, setText] = useState("");
  const [linkUrl, setLinkUrl] = useState("");
  const [isActive, setIsActive] = useState(true);
  const [sortOrder, setSortOrder] = useState(0);

  const [message, setMessage] = useState(null);
  const [error, setError] = useState(null);

  // Fetch all announcements
  const fetchAnnouncements = async () => {
    setLoading(true);
    try {
      const res = await API.get("/announcements");
      const items = Array.isArray(res?.announcements)
        ? res.announcements
        : Array.isArray(res)
          ? res
          : [];
      setAnnouncements(items);
    } catch (err) {
      console.error(err);
      setError("Failed to load announcements.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAnnouncements();
  }, []);

  // Handle Form Submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setMessage(null);

    if (!text.trim()) {
      setError("Announcement text is required.");
      return;
    }

    const payload = {
      text,
      link_url: linkUrl || null,
      is_active: isActive,
      sort_order: parseInt(sortOrder) || 0,
    };

    try {
      if (isEditing) {
        await API.put(`/announcements/${editId}`, payload);
        setMessage("Announcement updated successfully.");
      } else {
        await API.post("/announcements", payload);
        setMessage("Announcement created successfully.");
      }
      resetForm();
      fetchAnnouncements();
    } catch (err) {
      console.error(err);
      setError(err?.response?.data?.message || "Failed to save announcement.");
    }
  };

  // Populate form for editing
  const handleEdit = (item) => {
    setIsEditing(true);
    setEditId(item.id);
    setText(item.text);
    setLinkUrl(item.link_url || "");
    setIsActive(item.is_active);
    setSortOrder(item.sort_order);
  };

  // Toggle active status
  const handleToggleActive = async (item) => {
    try {
      const payload = {
        text: item.text,
        link_url: item.link_url,
        is_active: !item.is_active,
        sort_order: item.sort_order
      };
      await API.put(`/announcements/${item.id}`, payload);
      fetchAnnouncements();
    } catch (err) {
      console.error(err);
      setError("Failed to toggle status.");
    }
  };

  // Delete Announcement
  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this announcement?")) return;
    try {
      await API.delete(`/announcements/${id}`);
      setMessage("Announcement deleted successfully.");
      fetchAnnouncements();
    } catch (err) {
      console.error(err);
      setError("Failed to delete announcement.");
    }
  };

  // Reset form state
  const resetForm = () => {
    setIsEditing(false);
    setEditId(null);
    setText("");
    setLinkUrl("");
    setIsActive(true);
    setSortOrder(0);
  };

  return (
    <>
      <Navbar />
      <div className="p-4 sm:p-6 lg:p-8 max-w-7xl mx-auto">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-black text-slate-900 flex items-center gap-2.5">
            <FaBullhorn className="text-emerald-600" /> Ticker Alerts Manager
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Manage scrolling marquee announcements shown on the public landing page header.
          </p>
        </div>
      </div>

      {message && (
        <div className="mb-4 p-4 bg-emerald-50 border border-emerald-250 text-emerald-900 rounded-2xl font-semibold text-sm">
          {message}
        </div>
      )}

      {error && (
        <div className="mb-4 p-4 bg-rose-50 border border-rose-250 text-rose-900 rounded-2xl font-semibold text-sm">
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        
        {/* FORM CONTAINER */}
        <div className="lg:col-span-1 bg-white border border-slate-200 rounded-3xl p-6 shadow-sm h-fit">
          <h2 className="text-lg font-black text-slate-800 mb-4 flex items-center gap-2">
            {isEditing ? <FaEdit className="text-amber-500" /> : <FaPlus className="text-emerald-500" />}
            {isEditing ? "Edit Ticker Alert" : "Add New Ticker Alert"}
          </h2>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-xs font-bold text-slate-600 uppercase tracking-wider mb-1.5">
                Announcement Text
              </label>
              <textarea
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder="e.g. Apply now for the Parasports Excellence Awards! Click here to check guidelines..."
                className="w-full border border-slate-200 rounded-2xl px-4 py-3 text-sm focus:outline-none focus:border-emerald-500 min-h-[100px] resize-y"
                required
              />
            </div>

            <div>
              <label className="block text-xs font-bold text-slate-600 uppercase tracking-wider mb-1.5 flex items-center gap-1.5">
                <FaLink className="text-slate-400" /> Redirect URL (Optional)
              </label>
              <input
                type="text"
                value={linkUrl}
                onChange={(e) => setLinkUrl(e.target.value)}
                placeholder="e.g. /excellence-awards or https://google.com"
                className="w-full border border-slate-200 rounded-2xl px-4 py-2.5 text-sm focus:outline-none focus:border-emerald-500"
              />
              <p className="text-[10px] text-slate-400 mt-1">
                Relative app path (like /excellence-awards) or full web address starting with http/https.
              </p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-bold text-slate-600 uppercase tracking-wider mb-1.5 flex items-center gap-1.5">
                  <FaSortNumericDown className="text-slate-400" /> Sort Order
                </label>
                <input
                  type="number"
                  value={sortOrder}
                  onChange={(e) => setSortOrder(e.target.value)}
                  placeholder="0"
                  className="w-full border border-slate-200 rounded-2xl px-4 py-2 text-sm focus:outline-none focus:border-emerald-500"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-600 uppercase tracking-wider mb-1.5">
                  Active Status
                </label>
                <div className="flex items-center h-10">
                  <button
                    type="button"
                    onClick={() => setIsActive(!isActive)}
                    className={`flex items-center gap-2 font-bold text-xs rounded-full px-3 py-1.5 border transition-all ${
                      isActive
                        ? "bg-emerald-50 text-emerald-800 border-emerald-300"
                        : "bg-slate-50 text-slate-500 border-slate-200"
                    }`}
                  >
                    {isActive ? <FaToggleOn size={16} className="text-emerald-600" /> : <FaToggleOff size={16} />}
                    {isActive ? "Active" : "Inactive"}
                  </button>
                </div>
              </div>
            </div>

            <div className="flex gap-2.5 pt-2">
              <button
                type="submit"
                className="flex-1 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white font-bold text-sm rounded-full py-2.5 shadow-sm transition-all duration-300 flex items-center justify-center gap-2"
              >
                <FaSave /> Save Ticker
              </button>
              
              {isEditing && (
                <button
                  type="button"
                  onClick={resetForm}
                  className="bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold text-sm rounded-full px-4 py-2.5 transition-colors flex items-center justify-center"
                >
                  <FaTimes /> Cancel
                </button>
              )}
            </div>
          </form>
        </div>

        {/* LIST CONTAINER */}
        <div className="lg:col-span-2 bg-white border border-slate-200 rounded-3xl p-6 shadow-sm">
          <h2 className="text-lg font-black text-slate-800 mb-4">Current Scrolling Tickers</h2>
          
          {loading ? (
            <div className="py-12 text-center text-slate-500 font-bold">Loading...</div>
          ) : announcements.length === 0 ? (
            <div className="py-12 text-center text-slate-400 font-bold">
              No scrolling announcements found. The header ticker will fall back to default text.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="border-b border-slate-100">
                    <th className="py-3 text-xs font-black text-slate-500 uppercase tracking-wider pl-2 w-10">Sort</th>
                    <th className="py-3 text-xs font-black text-slate-500 uppercase tracking-wider">Announcement Text</th>
                    <th className="py-3 text-xs font-black text-slate-500 uppercase tracking-wider w-28">Status</th>
                    <th className="py-3 text-xs font-black text-slate-500 uppercase tracking-wider w-24 text-right pr-2">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-50">
                  {announcements.map((item) => (
                    <tr key={item.id} className="hover:bg-slate-50/50 transition-colors">
                      <td className="py-4 pl-2 font-bold text-slate-500 text-sm">{item.sort_order}</td>
                      <td className="py-4 pr-4">
                        <div className="text-sm font-semibold text-slate-800 line-clamp-2 leading-relaxed">
                          {item.text}
                        </div>
                        {item.link_url && (
                          <div className="text-[10px] text-teal-600 font-bold flex items-center gap-1 mt-1">
                            <FaLink size={10} /> {item.link_url}
                          </div>
                        )}
                      </td>
                      <td className="py-4">
                        <button
                          onClick={() => handleToggleActive(item)}
                          className={`flex items-center gap-1.5 text-xs font-bold px-2.5 py-1 rounded-full border transition-all ${
                            item.is_active
                              ? "bg-emerald-50 border-emerald-250 text-emerald-800"
                              : "bg-slate-100 border-slate-200 text-slate-400"
                          }`}
                        >
                          {item.is_active ? <FaToggleOn size={14} className="text-emerald-600" /> : <FaToggleOff size={14} />}
                          {item.is_active ? "Active" : "Inactive"}
                        </button>
                      </td>
                      <td className="py-4 text-right pr-2">
                        <div className="flex items-center justify-end gap-1.5">
                          <button
                            onClick={() => handleEdit(item)}
                            title="Edit"
                            className="p-2 text-slate-400 hover:text-amber-500 hover:bg-amber-50 rounded-xl transition-all"
                          >
                            <FaEdit size={14} />
                          </button>
                          <button
                            onClick={() => handleDelete(item.id)}
                            title="Delete"
                            className="p-2 text-slate-400 hover:text-rose-500 hover:bg-rose-50 rounded-xl transition-all"
                          >
                            <FaTrash size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
    </>
  );
};

export default ManageAnnouncements;
